import { getAuth } from "firebase/auth";

const BASE = import.meta.env.VITE_API_URL;

async function authHeaders(): Promise<HeadersInit> {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

// ── Types ──────────────────────────────────────────────────────────────────

export enum CallDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

export enum CallStatus {
  RINGING = 'ringing',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  MISSED = 'missed',
  FAILED = 'failed',
  BUSY = 'busy',
  NO_ANSWER = 'no_answer',
}

export interface CallLog {
  id: string;
  direction: CallDirection;
  status: CallStatus;
  from: string;
  to: string;
  duration?: number;
  recordingUrl?: string;
  transcription?: string;
  notes?: string;
  userId: string;
  dealId?: string;
  twilioCallSid?: string;
  createdAt: string;
  endedAt?: string;
}

export interface InitiateCallDto {
  to: string;
  dealId?: string;
  notes?: string;
}

export interface UpdateCallDto {
  status?: CallStatus;
  notes?: string;
  recordingUrl?: string;
  transcription?: string;
  endedAt?: string;
}

export interface FilterCallsDto {
  direction?: CallDirection;
  status?: CallStatus;
  dealId?: string;
  fromDate?: string;
  toDate?: string;
}

export interface TwilioTokenResponse {
  token: string;
  identity: string;
}

// ── API ────────────────────────────────────────────────────────────────────

export const callsApi = {
  /**
   * Get Twilio Voice JWT token for softphone
   */
  getToken: async (): Promise<TwilioTokenResponse> => {
    const res = await fetch(`${BASE}/calls/token`, {
      headers: await authHeaders(),
    });
    if (!res.ok) throw new Error(`Token fetch failed: ${res.status}`);
    return res.json();
  },

  /**
   * Initiate an outbound call
   */
  initiateCall: async (payload: InitiateCallDto): Promise<CallLog> => {
    const res = await fetch(`${BASE}/calls/initiate`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Call initiation failed: ${res.status}`);
    return res.json();
  },

  /** Alias for backward compat */
  initiate: async (payload: InitiateCallDto): Promise<CallLog> => {
    return callsApi.initiateCall(payload);
  },

  /**
   * End an active call
   */
  endCall: async (callId: string): Promise<{ ok: boolean }> => {
    const res = await fetch(`${BASE}/calls/${callId}/end`, {
      method: "PATCH",
      headers: await authHeaders(),
      body: JSON.stringify({
        status: "completed",
        endedAt: new Date().toISOString(),
      }),
    });
    if (!res.ok) throw new Error(`End call failed: ${res.status}`);
    return res.json();
  },

  /**
   * Update call notes / metadata
   */
  updateCall: async (
    callId: string,
    updates: UpdateCallDto
  ): Promise<CallLog> => {
    const res = await fetch(`${BASE}/calls/${callId}`, {
      method: "PATCH",
      headers: await authHeaders(),
      body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error(`Update call failed: ${res.status}`);
    return res.json();
  },

  /** Alias for backward compat */
  update: async (callId: string, updates: UpdateCallDto): Promise<CallLog> => {
    return callsApi.updateCall(callId, updates);
  },

  /** Get single call by id */
  getById: async (id: string): Promise<CallLog> => {
    const res = await fetch(`${BASE}/calls/${id}`, {
      headers: await authHeaders(),
    });
    if (!res.ok) throw new Error(`Get call failed: ${res.status}`);
    return res.json();
  },

  /**
   * List calls with optional filters
   */
  listCalls: async (filters?: FilterCallsDto): Promise<CallLog[]> => {
    const params = new URLSearchParams(
      Object.entries(filters || {})
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    );
    const res = await fetch(`${BASE}/calls?${params}`, {
      headers: await authHeaders(),
    });
    if (!res.ok) throw new Error(`List calls failed: ${res.status}`);
    return res.json();
  },

  /** Alias for backward compat */
  list: async (filters?: FilterCallsDto): Promise<CallLog[]> => {
    return callsApi.listCalls(filters);
  },
};
